import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { PasswordInput } from '@/components/PasswordInput';
import { Button } from '@/components/Button';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import type { DeleteAccountFailure } from '@/lib/db/buyer';

import {
  DB_LANG_TO_UI,
  UI_LANG_TO_DB,
  useChangePassword,
  useDeleteAccountRequest,
  useUpdateProfile,
} from './DashboardProfilePage.data';

type UiLang = 'EN' | 'FR' | 'AR';
type DangerStep = 'idle' | 'consequences' | 'confirm';

const PASSWORD_MIN = 8;

function deleteFailureToI18nKey(failure: DeleteAccountFailure): string {
  switch (failure.errorCode) {
    case 'unauthenticated':
      return 'buyer.profile.danger.errors.unauthenticated';
    case 'already_deleted':
      return 'buyer.profile.danger.errors.alreadyDeleted';
    default:
      return 'buyer.profile.danger.errors.internalError';
  }
}

export function DashboardProfilePage() {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const navigate = useNavigate();
  const auth = useAuth();

  const profile = auth.status === 'authenticated' ? auth.profile : null;
  const userId = auth.status === 'authenticated' ? auth.user.id : null;
  const email = auth.status === 'authenticated' ? auth.user.email : '';

  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [lastName, setLastName] = useState(profile?.last_name ?? '');
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const initialUiLang: UiLang = profile?.language_preference
    ? DB_LANG_TO_UI[profile.language_preference as 'en' | 'fr' | 'ar']
    : 'EN';
  const [uiLang, setUiLang] = useState<UiLang>(initialUiLang);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [dangerStep, setDangerStep] = useState<DangerStep>('idle');

  const { update: updateUser } = useUpdateProfile();
  const { change: changeUserPassword } = useChangePassword();
  const { run: runDeleteAccount } = useDeleteAccountRequest();

  const handlePersonalSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!userId) return;
    try {
      await updateUser({
        userId,
        first_name: firstName,
        last_name: lastName,
        phone: phone || null,
      });
      toast.success(t('buyer.profile.savedToast'));
    } catch {
      toast.error(t('buyer.errors.generic'));
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword.length < PASSWORD_MIN) {
      setPasswordError(t('buyer.profile.password.tooShort', { min: PASSWORD_MIN }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('buyer.profile.password.mismatch'));
      return;
    }
    setPasswordError(null);
    try {
      await changeUserPassword(newPassword);
      toast.success(t('buyer.profile.password.successToast'));
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toast.error(t('buyer.errors.generic'));
    }
  };

  const handleLanguageChange = async (lang: UiLang) => {
    if (lang === uiLang || !userId) return;
    setUiLang(lang);
    const dbLang = UI_LANG_TO_DB[lang];
    try {
      await updateUser({ userId, language_preference: dbLang });
      await i18n.changeLanguage(dbLang);
      toast.success(t('buyer.profile.preferences.langChangedToast'));
    } catch {
      // Roll back the UI toggle on failure.
      setUiLang(initialUiLang);
      toast.error(t('buyer.errors.generic'));
    }
  };

  const handleDangerConfirm = async () => {
    const failure = await runDeleteAccount(() => navigate('/', { replace: true }));
    if (failure) {
      setDangerStep('idle');
      toast.error(t(deleteFailureToI18nKey(failure)));
      return;
    }
    toast.success(t('buyer.profile.danger.deletedToast'));
    setDangerStep('idle');
  };

  return (
    <>
      <header className="buyer-page-head">
        <h1 className="buyer-page-head__title">{t('buyer.profile.title')}</h1>
      </header>

      <div className="buyer-grid--2col">
        <section className="buyer-card">
          <div className="buyer-card__kicker">{t('buyer.profile.personalInfo')}</div>
          <form className="buyer-card__form" onSubmit={(event) => void handlePersonalSubmit(event)}>
            <div className="buyer-card__row">
              <TextField
                label={t('buyer.profile.firstName')}
                name="firstName"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
              />
              <TextField
                label={t('buyer.profile.lastName')}
                name="lastName"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
              />
            </div>
            <TextField
              label={t('buyer.profile.email')}
              type="email"
              name="email"
              value={email}
              disabled
              helper={t('buyer.profile.emailReVerify')}
            />
            <PhoneField
              label={t('buyer.profile.phone')}
              name="phone"
              value={phone ?? ''}
              onChange={setPhone}
            />
            <Button type="submit" variant="primary">
              {t('buyer.profile.save')}
            </Button>
          </form>
        </section>

        <section className="buyer-card">
          <div className="buyer-card__kicker">{t('buyer.profile.password.kicker')}</div>
          <form className="buyer-card__form" onSubmit={(event) => void handlePasswordSubmit(event)}>
            <PasswordInput
              label={t('buyer.profile.password.new')}
              name="newPassword"
              autoComplete="new-password"
              placeholder={t('buyer.profile.password.newPlaceholder')}
              showLabel={t('auth:common.passwordShow')}
              hideLabel={t('auth:common.passwordHide')}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <PasswordInput
              label={t('buyer.profile.password.confirm')}
              name="confirmPassword"
              autoComplete="new-password"
              placeholder={t('buyer.profile.password.confirmPlaceholder')}
              showLabel={t('auth:common.passwordShow')}
              hideLabel={t('auth:common.passwordHide')}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
            {passwordError ? (
              <p role="alert" className="buyer-card__error">
                {passwordError}
              </p>
            ) : null}
            <Button type="submit" variant="primary">
              {t('buyer.profile.password.submit')}
            </Button>
          </form>
        </section>

        <section className="buyer-card">
          <div className="buyer-card__kicker">{t('buyer.profile.preferences.kicker')}</div>
          <div className="buyer-prefs__row">
            <span className="buyer-prefs__label">{t('buyer.profile.preferences.language')}</span>
            <div className="buyer-prefs__lang">
              {(['EN', 'FR', 'AR'] as const).map((lang) => (
                <button
                  key={lang}
                  type="button"
                  className={`buyer-prefs__lang-btn${uiLang === lang ? ' is-active' : ''}`}
                  onClick={() => void handleLanguageChange(lang)}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="buyer-card buyer-card--danger">
          <div className="buyer-card__kicker">{t('buyer.profile.danger.kicker')}</div>
          <p className="buyer-card__danger-body">{t('buyer.profile.danger.lead')}</p>
          <button
            type="button"
            className="buyer-card__danger-trigger"
            onClick={() => setDangerStep('consequences')}
          >
            {t('buyer.profile.danger.trigger')}
          </button>
        </section>
      </div>

      {dangerStep === 'consequences' && (
        <ConfirmModal
          title={t('buyer.profile.danger.trigger')}
          body={t('buyer.profile.danger.step1.consequences')}
          confirmLabel={t('buyer.profile.danger.step1.continue')}
          cancelLabel={t('buyer.profile.danger.step1.cancel')}
          danger
          onConfirm={() => setDangerStep('confirm')}
          onCancel={() => setDangerStep('idle')}
        />
      )}

      {dangerStep === 'confirm' && (
        <ConfirmModal
          title={t('buyer.profile.danger.trigger')}
          body={t('buyer.profile.danger.step2.instruction')}
          confirmLabel={t('buyer.profile.danger.step2.confirm')}
          cancelLabel={t('buyer.profile.danger.step2.cancel')}
          danger
          requireType="DELETE"
          onConfirm={() => void handleDangerConfirm()}
          onCancel={() => setDangerStep('idle')}
        />
      )}
    </>
  );
}
